import { ErrorMessage, Field, Form, Formik, FormikHelpers, FormikState } from "formik";
import React, { useEffect, useRef, useState } from "react";
import { Button, Col, Modal, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { normalizeCategoryData } from "../../Products/services/normalizeCategoryData";
import { ApolloError, useApolloClient, useReactiveVar } from "@apollo/client";
import { CategoryInterface, Option, unitGroupI } from "../../Products/types";
import { PriceInput, ReactSelect, SpinnerLoader } from "@services/ui-components";
import { getCombinedUnitOptions, productFormValidationSchema } from "./utils";
import {
    CompanyAccess,
    ProductCategoryNewInput,
    ProductCategoryNewProxy,
    ProductInput,
    ProductProxy,
    UnitProxy,
} from "@me-team/host/main/graphql/types";
import { useCreateProductToWarehouseMutation } from "@me-team/host/main/addNewProductList/graphql/addNewProductList.hooks";
import { useCreateStorageProductRelationMutation } from "@me-team/host/main/warehouses_goods/graphql/warehouses_goods.hooks";
import {
    GetBrandsDocument,
    GetProductCategoriesDocument,
    useCreateProductCategoryMutation,
    useGeMainUnitsForCreateUpdateProductQuery,
    useGetAlternativeUnitGroupsForCreateUpdateProductQuery,
    useGetProductCategoriesQuery,
    useGetUnitsQuery,
} from "@me-team/host/main/products/graphql/products.hooks";
import QuantityInput from "./QuantityInput";
import { formatDateDMY } from "@me-team/host/src/utils/utils";
import { currentCompanyIdVar } from "@me-team/host/main/globalVar/state";
import moment from "moment";
import CustomCreatableReactSelect from "@services/ui-components/src/select/CustomCreatableReactSelect";
import { useGetSuppliersQuery } from "@me-team/host/main/warehouses/graphql/warehouses.hooks";
import { useNavigate, useParams } from "react-router-dom";
import {
    ALT_UNIT_GROUP_NONE_ID,
    clearAltErrors,
    clearAltFields,
    getAutoAltUnitId,
    getMainUnitGroup,
    getMainUnitOptions,
    getUnitLabelByValue,
    getUnitNameById,
    unitGroupQuantityId,
    unitPCId,
} from "../../Products/constants";
import NumberInput from "../../Products/inputs/NumberInput";
import { ServiceModalWrapper } from "@me-pos/service-modal";
import i18next from "i18next";
import CategoryProduct from "../../Products/services/CategoryProduct";
import RequestInputService from "../../Products/services/RequestInputService";
import {
    isWarehouseTutorialActive,
    moveStepInLocalStorage,
    setStepInLocalStorage,
} from "../../Joyride/WarehouseTutorial/joyrideStepHelpers";
import { LOCAL_STORAGE_CREATED_ID } from "../../Joyride/WarehouseTutorial/constants";
import { AddProductListFormEffects } from "./AddProductListFormEffects";
import { useRoleAccess } from "@me-team/host/src/utils/useRoleAccess";

export type ProductInputWithQuantity = ProductInput & {
    quantity: string;
    comment?: string;
    supplier?: string;
    unitForSale?: number;
};

type ProductInputExistingInitialValues = Omit<ProductInput, "unitGroup"> & {
    unitGroup: string;
    quantity: string;
    comment?: string;
    supplier?: string;
    unitForSale?: number;
};

const AddProductListModal: React.FC<{
    show: boolean;
    onHide: () => void;
    refetch?: () => void;
    handleModal: (arg?: any) => void;
    existingProduct: ProductProxy;
    setExistingProduct: (ProductProxy?: any) => void;
    storageId: number;
    toggleShowToast?: (text: string) => void;
    onDuplicateProductModal?: (productId: number) => void;
    setProductForChange: (product: ProductProxy) => void;
    productForChange: ProductProxy;
    dialogClassName?: string;
    centered?: boolean;
    dataTour?: string;
}> = ({
    show,
    refetch,
    handleModal,
    existingProduct,
    setExistingProduct,
    storageId,
    toggleShowToast,
    onDuplicateProductModal,
    setProductForChange,
    productForChange,
    centered = true,
    dialogClassName,
    dataTour,
}) => {
    const { t } = useTranslation();
    const { branchId, id } = useParams();
    const navigate = useNavigate();
    const client = useApolloClient();
    const currentCompanyId = useReactiveVar(currentCompanyIdVar);
    const canCreateAndAddProduct = useRoleAccess(CompanyAccess.WarehouseProductCreateAndAdd);
    const canAddProduct = useRoleAccess(CompanyAccess.WarehouseProductAdd);
    const canCreateProductCategory = useRoleAccess(CompanyAccess.ProductCategoryCreate);
    const canSubmit = existingProduct ? canAddProduct : canCreateAndAddProduct;
    const buttonCategoryRef = useRef<HTMLButtonElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const tutorialActive = isWarehouseTutorialActive();

    const [unitOfMeasurements, setUnitOfMeasurements] = useState([]);
    const [isProductCategoryModalOpen, setIsProductCategoryModalOpen] = useState<boolean>(false);
    const [currentCategory, setCurrentCategory] = useState(null);
    const [categoryList, setCategoryList] = useState<CategoryInterface[]>(null);
    const [categoryInput, setCategoryInput] = useState(null);
    const [pickedCategoryId, setPickedCategoryId] = useState<number>(null);
    const [selectedMainUnitId, setSelectedMainUnitId] = useState<number>(null);
    const [relationsAlternativeUnits, setRelationsAlternativeUnits] = useState<UnitProxy[]>(null);
    useEffect(() => {
        if (contentRef.current && dataTour) {
            contentRef.current.setAttribute("data-tour", dataTour);
        }
    }, [dataTour, show]);

    const [categoryText, setCategoryText] = useState([null, null]);
    const selectedCategory = categoryText[0] || "";
    const selectedSubcategory = categoryText[1] || "";

    const [createProductCategory] = useCreateProductCategoryMutation();

    const refetchActiveProductCategories = () => {
        return client.refetchQueries({
            include: [GetProductCategoriesDocument],
        });
    };

    const {
        data: productCategoriesData,
        refetch: refetchCategories,
        loading: productCategoryLoading,
    } = useGetProductCategoriesQuery({
        variables: {
            itemsPerPage: 10000,
            delete: false,
            branchId: Number(branchId),
        },
        context: {
            errorType: "local",
        },
        onCompleted: (data) => {
            const normalizedItems = normalizeCategoryData(data);
            setCategoryList(CategoryProduct.categoryModuleCreator(normalizedItems, null, null));
            setCategoryInput(RequestInputService.createServiceCategoryInput(null, null, normalizedItems));
        },
        onError: (error: ApolloError) => {
            console.error("error:", error);
        },
    });

    const categoryListData: ProductCategoryNewProxy[] = productCategoriesData?.productCategories?.items;

    const handleSubmitCreateCategory = async (input: ProductCategoryNewInput) => {
        if (!canCreateProductCategory) return;

        try {
            currentCompanyId &&
                (await createProductCategory({
                    variables: {
                        companyId: +currentCompanyId,
                        input: input,
                        branchId: Number(branchId),
                    },
                    context: {
                        errorType: "local",
                    },
                    onCompleted: async (data) => {
                        data && setCurrentCategory(data.createProductCategoryNew);
                        await refetchActiveProductCategories();
                    },
                    onError: (error) => {
                        console.error("error:", error);
                    },
                }));
        } catch (error) {
            console.error(error);
        }
    };

    const handleCategoryNameChange = (categoryName: string) => {
        setCategoryText([categoryName]);
    };

    const handleSubCategoryNameChange = (subCategoryName: string, categoryName: string) => {
        setCategoryText([categoryName, subCategoryName]);
    };

    const resetInitialStateCategory = () => {
        setCategoryText([null, null]);
        setPickedCategoryId(null);
    };

    const initialValues: ProductInputWithQuantity = {
        name: productForChange?.name ? productForChange.name : "",
        brand: productForChange?.brand ? productForChange.brand : "",
        category: productForChange?.productCategoryNew?.id ? productForChange.productCategoryNew.id : null,
        sku: productForChange?.sku ? productForChange.sku : "",
        barcode: productForChange?.barcode ? productForChange.barcode : "",
        unitGroup: unitGroupQuantityId,
        unitForSale: unitPCId,
        unit: null,
        quantity: null,
        comment: "",
        supplier: null,
        price: null,
        cost: null,
        alternativeUnitGroupInput: {
            alternativeUnitGroup: null,
            alternativeUnit: null,
            alternativeValue: null,
        },
    };

    const existingInitialValues: ProductInputExistingInitialValues = {
        name: existingProduct?.name,
        brand: existingProduct?.brand,
        category: existingProduct?.productCategoryNew?.id,
        sku: existingProduct?.sku,
        barcode: existingProduct?.barcode,
        unitGroup: existingProduct?.unitGroup?.name,
        unit: null,
        quantity: "",
        comment: "",
        price: Number(existingProduct?.price),
    };
    const [createProduct] = useCreateProductToWarehouseMutation();
    const [createStorageProductRelation] = useCreateStorageProductRelationMutation({
        context: {
            errorType: "local",
        },
    });

    const { data: mainUnitsData } = useGeMainUnitsForCreateUpdateProductQuery({
        context: {
            errorType: "local",
        },
        onError: (error: ApolloError) => {
            console.error("error:", error);
        },
    });
    const { data: alternativeUnitGroupsData, refetch: alternativeUnitGroupsRefetch } =
        useGetAlternativeUnitGroupsForCreateUpdateProductQuery({
            variables: { mainUnitId: selectedMainUnitId },
            skip: !selectedMainUnitId,
            context: {
                errorType: "local",
            },
            onError: (error: ApolloError) => {
                console.error("error:", error);
            },
        });
    const { data: unitsData } = useGetUnitsQuery({
        context: {
            errorType: "local",
        },
        onError: (error: ApolloError) => {
            console.error("error:", error);
        },
    });

    const handleMainUnit = (
        selectedOption: Option,
        setFieldValue: FormikHelpers<ProductInput>["setFieldValue"],
        setFieldTouched: FormikHelpers<ProductInput>["setFieldTouched"],
        setFieldError: FormikHelpers<ProductInput>["setFieldError"]
    ) => {
        const nextMainUnitId: number = selectedOption.value;
        setFieldValue("unitForSale", nextMainUnitId);

        setRelationsAlternativeUnits([]);

        alternativeUnitGroupsRefetch();

        clearAltFields(setFieldValue);
        setFieldTouched("alternativeUnitGroupInput.alternativeUnitGroup", false, false);
        setFieldTouched("alternativeUnitGroupInput.alternativeUnit", false, false);
        setFieldTouched("alternativeUnitGroupInput.alternativeValue", false, false);
        clearAltErrors(setFieldError);
    };

    const unitOptions: Option[] = getMainUnitOptions(mainUnitsData?.relations?.mainUnits);

    const alternativeUnitGroupOptions: Option[] = [
        ...((alternativeUnitGroupsData?.relations?.alternativeUnitGroups ?? []).map((unitGroup: unitGroupI) => ({
            value: unitGroup.id,
            label: unitGroup.name,
        })) as Option[]),
        { value: ALT_UNIT_GROUP_NONE_ID, label: t("none") },
    ];

    const handleAlternativeUnitGroupChange = (
        selectedOption: Option,
        setFieldValue: FormikHelpers<ProductInput>["setFieldValue"],
        setFieldTouched: FormikHelpers<ProductInput>["setFieldTouched"],
        setFieldError: FormikHelpers<ProductInput>["setFieldError"],
        mainUnitId: number | null
    ) => {
        const nextGroupId =
            selectedOption?.value !== undefined && selectedOption?.value !== null ? Number(selectedOption.value) : null;

        setFieldValue("alternativeUnitGroupInput.alternativeUnitGroup", nextGroupId, false);

        clearAltErrors(setFieldError);

        if (nextGroupId === ALT_UNIT_GROUP_NONE_ID) {
            const autoUnitId = getAutoAltUnitId(mainUnitId);
            setRelationsAlternativeUnits([]);

            setFieldValue("alternativeUnitGroupInput.alternativeUnit", autoUnitId, false);
            setFieldValue("alternativeUnitGroupInput.alternativeValue", 1, false);
            setFieldTouched("alternativeUnitGroupInput.alternativeUnitGroup", false, false);
            setFieldTouched("alternativeUnitGroupInput.alternativeUnit", false, false);
            setFieldTouched("alternativeUnitGroupInput.alternativeValue", false, false);

            return;
        }

        const filteredAlternativeUnits =
            unitsData?.relations?.units?.filter((unit) => unit.unitGroup.id === nextGroupId) || [];

        setRelationsAlternativeUnits(filteredAlternativeUnits);

        if (filteredAlternativeUnits.length > 0) {
            setFieldValue("alternativeUnitGroupInput.alternativeUnit", filteredAlternativeUnits[0].id, false);
        } else {
            setFieldValue("alternativeUnitGroupInput.alternativeUnit", null, false);
        }

        setFieldValue("alternativeUnitGroupInput.alternativeValue", null, false);

        setFieldError("alternativeUnitGroupInput.alternativeUnitGroup", undefined);
        setFieldError("alternativeUnitGroupInput.alternativeUnit", undefined);
        setFieldError("alternativeUnitGroupInput.alternativeValue", undefined);
    };

    const handleAlternativeUnitChange = (
        selectedOption: Option,
        setFieldValue: FormikHelpers<ProductInput>["setFieldValue"]
    ) => {
        setFieldValue("alternativeUnitGroupInput.alternativeUnit", selectedOption.value);
    };

    useEffect(() => {
        if (existingProduct) {
            const optionsUnitOfMeasurements =
                existingProduct?.units?.map((unit) => ({
                    value: unit.id,
                    label: unit.name,
                })) || [];
            setUnitOfMeasurements(optionsUnitOfMeasurements);
        }
    }, [existingProduct]);

    const handleOpenDuplicateProductModal = (productId: number) => {
        onDuplicateProductModal(productId);
    };

    const { data: branchProductSuppliersData, refetch: suppliersRefetch } = useGetSuppliersQuery({
        variables: {
            companyId: +currentCompanyId,
            branchId: Number(branchId),
        },
        skip: !currentCompanyId || !branchId,
    });

    const productSupplierOptions =
        branchProductSuppliersData?.suppliers.map((supplier) => {
            return {
                id: supplier.id,
                name: supplier.name,
            };
        }) || [];

    const handleSubmit = async (values: typeof initialValues, { resetForm }: FormikHelpers<typeof initialValues>) => {
        if (!canSubmit) return;

        try {
            if (existingProduct) {
                await createStorageProductRelation({
                    variables: {
                        storageId: +storageId,
                        productId: existingProduct.id,
                        input: {
                            quantity: +values?.quantity,
                            unit: values.unit,
                            supplier: values.supplier,
                        },
                        branchId: Number(branchId),
                    },
                    onCompleted: async (data) => {
                        await suppliersRefetch();
                        tutorialActive &&
                            localStorage.setItem(
                                LOCAL_STORAGE_CREATED_ID,
                                String(data.createStorageProductRelation.product.id)
                            );
                        tutorialActive && setStepInLocalStorage(7);
                        tutorialActive && navigate(`/warehouses/warehouses-goods/branch/${branchId}/${id}`);
                    },
                });
            } else {
                const { quantity, comment, supplier, unit, unitForSale, alternativeUnitGroupInput, ...rest } = values;
                const filterId = values.category || null;
                const isNone = alternativeUnitGroupInput?.alternativeUnitGroup === ALT_UNIT_GROUP_NONE_ID;
                const getAlternativeUnitGroupInput = isNone
                    ? null
                    : alternativeUnitGroupInput?.alternativeUnitGroup == null &&
                        alternativeUnitGroupInput?.alternativeUnit == null &&
                        alternativeUnitGroupInput?.alternativeValue == null
                      ? null
                      : {
                            alternativeUnitGroup: alternativeUnitGroupInput?.alternativeUnitGroup,
                            alternativeUnit: alternativeUnitGroupInput?.alternativeUnit,
                            alternativeValue: alternativeUnitGroupInput?.alternativeValue,
                        };
                const input = {
                    ...rest,
                    category: filterId,
                    unit: unitForSale,
                    unitGroup: getMainUnitGroup(values?.unitForSale),
                    alternativeUnitGroupInput: getAlternativeUnitGroupInput,
                };
                const response = await createProduct({
                    variables: { companyId: +currentCompanyId, input, branchId: Number(branchId) },
                    refetchQueries: [
                        {
                            query: GetBrandsDocument,
                            variables: {
                                companyId: Number(currentCompanyId),
                                branchId: Number(branchId),
                            },
                        },
                    ],
                });
                if (response?.data?.createProduct?.id) {
                    await createStorageProductRelation({
                        variables: {
                            storageId: +storageId,
                            productId: response?.data?.createProduct.id,
                            input: {
                                quantity: +quantity,
                                unit: values.unit,
                                comment: values.comment,
                                supplier: values.supplier,
                                product: {
                                    name: values.name,
                                    brand: values.brand,
                                    category: values.category,
                                    unitGroup: response?.data?.createProduct?.unitGroup?.id,
                                    unit: response?.data?.createProduct?.unit?.id,
                                    alternativeUnitGroupInput: getAlternativeUnitGroupInput,
                                    price: values?.price,
                                    cost: values.cost,
                                },
                            },
                            branchId: Number(branchId),
                        },
                        onError: (error) => {
                            console.error(error);
                        },
                    });
                    await refetchActiveProductCategories();
                }
            }
            refetch();
            suppliersRefetch();
            handleResetForm(resetForm);
            toggleShowToast(existingProduct ? t("Saved") : t("Created"));
        } catch (error) {
            const graphQLError = error.graphQLErrors?.[0].extensions.validation[""][0].message;
            const productExistsRegex = /^The product already exists with name.*id = "(\d+)"/;
            const match = graphQLError.match(productExistsRegex);
            if (match) {
                const productId: number = +match[1];
                handleOpenDuplicateProductModal(productId);
                handleModal();
                setExistingProduct(null);
            }
            console.error(error);
        }
    };

    const handleResetForm = (resetForm: (nextState?: Partial<FormikState<ProductInputWithQuantity>>) => void) => {
        tutorialActive && moveStepInLocalStorage(-1);
        setProductForChange(null);
        setExistingProduct(null);
        resetInitialStateCategory();
        setRelationsAlternativeUnits(null);
        handleModal();
        resetForm();
    };

    return (
        <>
            <Formik
                initialValues={existingProduct ? existingInitialValues : initialValues}
                validationSchema={productFormValidationSchema(t, !!existingProduct)}
                enableReinitialize
                onSubmit={handleSubmit}
            >
                {({
                    values,
                    errors,
                    touched,
                    setFieldValue,
                    resetForm,
                    isSubmitting,
                    setFieldTouched,
                    setFieldError,
                }) => {
                    const selectedGroupId = values?.alternativeUnitGroupInput?.alternativeUnitGroup;

                    const combinedUnitOptions = getCombinedUnitOptions(
                        values?.unitForSale,
                        unitOptions,
                        relationsAlternativeUnits
                    );

                    const isAltNoneSelected = selectedGroupId === ALT_UNIT_GROUP_NONE_ID;
                    const isGroupNull = selectedGroupId == null;
                    let alternativeUnitOptions: Option[] = [];
                    if (isAltNoneSelected) {
                        const id = getAutoAltUnitId(values.unitForSale);
                        const label = getUnitNameById(id, unitsData?.relations?.units);
                        alternativeUnitOptions = [{ value: id, label }];
                    } else {
                        alternativeUnitOptions =
                            relationsAlternativeUnits?.map((unit: unitGroupI) => {
                                return {
                                    value: unit?.id,
                                    label: unit?.name,
                                };
                            }) || [];
                    }

                    const disableAltFields = isAltNoneSelected || isGroupNull;

                    return (
                        <>
                            <AddProductListFormEffects
                                pickedCategoryId={pickedCategoryId}
                                unitForSale={values?.unitForSale ?? null}
                                setSelectedMainUnitId={setSelectedMainUnitId}
                            />
                            <Modal
                                size="lg"
                                show={isProductCategoryModalOpen ? false : show}
                                onHide={() => handleResetForm(resetForm)}
                                centered={centered}
                                dialogClassName={dialogClassName ? dialogClassName : ""}
                                onEntered={() => {
                                    tutorialActive &&
                                        requestAnimationFrame(() => window.dispatchEvent(new Event("resize")));
                                }}
                            >
                                <div ref={contentRef}>
                                    <Modal.Header className="border-0" closeButton>
                                        <h5 className="m-0">
                                            {existingProduct
                                                ? t("Add products to the warehouse")
                                                : t("Create & Add product to the warehouse")}
                                        </h5>
                                    </Modal.Header>
                                    <Form>
                                        <Modal.Body style={{ padding: "16px 28px 16px 28px" }}>
                                            <Row>
                                                <Col sm={3} xs={4}>
                                                    <div className="text-dark fw-bold fs-7">
                                                        {t("Date and time of creation of the transaction")}
                                                    </div>
                                                </Col>
                                                <Col sm={9} xs={8}>
                                                    <div className="text-dark fs-7">
                                                        {formatDateDMY(moment().format("YYYY-MM-DD HH:mm:ss"))}
                                                    </div>
                                                </Col>
                                            </Row>
                                            <Row className="pt-3">
                                                <Col
                                                    sm={3}
                                                    xs={existingProduct ? 4 : 12}
                                                    className={`pe-0 d-flex align-items-center ${existingProduct ? "" : "mb-2 mb-sm-0"}`}
                                                >
                                                    <label htmlFor="name-addProduct">
                                                        <strong
                                                            className={`text-dark fs-7 ${existingProduct ? "" : "required-label"}`}
                                                        >
                                                            {t("Product name")}
                                                        </strong>
                                                    </label>
                                                </Col>
                                                <Col sm={9} xs={existingProduct ? 8 : 12} className="text-dark ">
                                                    {existingProduct ? (
                                                        <span className="fs-7">{values?.name}</span>
                                                    ) : (
                                                        <>
                                                            <Field
                                                                name="name"
                                                                id="name-addProduct"
                                                                className={`form-control fs-7 ${touched.name && errors.name ? "is-invalid" : ""}`}
                                                                placeholder={t("Enter the Product Name")}
                                                                maxLength={50}
                                                            />
                                                        </>
                                                    )}
                                                </Col>
                                            </Row>
                                            <Row className="p-0">
                                                <Col
                                                    sm={3}
                                                    xs={existingProduct ? 4 : 12}
                                                    className="pe-0 d-flex align-items-center"
                                                ></Col>
                                                <Col sm={9} xs={existingProduct ? 8 : 12} className="text-dark ">
                                                    {existingProduct ? (
                                                        ""
                                                    ) : (
                                                        <>
                                                            <span
                                                                className={`fs-7 ${values?.name?.length > 50 ? "text-danger" : "text-grey"}`}
                                                            >
                                                                {values?.name?.length}/50
                                                            </span>
                                                            <ErrorMessage name="name">
                                                                {(msg) => (
                                                                    <div className="invalid-feedback d-block">
                                                                        {msg}
                                                                    </div>
                                                                )}
                                                            </ErrorMessage>
                                                        </>
                                                    )}
                                                </Col>
                                            </Row>
                                            <Row className="pt-3">
                                                <Col
                                                    sm={3}
                                                    xs={existingProduct ? 4 : 12}
                                                    className={`pe-0 d-flex align-items-center ${existingProduct ? "" : "mb-2 mb-sm-0"}`}
                                                >
                                                    <label htmlFor="brand-addProduct">
                                                        <strong
                                                            className={`text-dark fs-7 ${existingProduct ? "" : "required-label"}`}
                                                        >
                                                            {t("ProductsList.Brand")}
                                                        </strong>
                                                    </label>
                                                </Col>
                                                <Col sm={9} xs={existingProduct ? 8 : 12} className="text-dark">
                                                    {existingProduct ? (
                                                        <span className="fs-7">{values?.brand}</span>
                                                    ) : (
                                                        <>
                                                            <Field
                                                                name="brand"
                                                                id="brand-addProduct"
                                                                className={`form-control fs-7 ${touched.brand && errors.brand ? "is-invalid" : ""}`}
                                                                placeholder={t("Enter a Brand")}
                                                                maxLength={50}
                                                            />
                                                        </>
                                                    )}
                                                </Col>
                                            </Row>
                                            <Row className="p-0">
                                                <Col
                                                    sm={3}
                                                    xs={existingProduct ? 4 : 12}
                                                    className="pe-0 d-flex align-items-center"
                                                ></Col>
                                                <Col sm={9} xs={existingProduct ? 8 : 12} className="text-dark ">
                                                    {existingProduct ? (
                                                        ""
                                                    ) : (
                                                        <>
                                                            <span
                                                                className={`fs-7 ${values?.brand?.length > 50 ? "text-danger" : "text-grey"} `}
                                                            >
                                                                {values.brand.length}/50
                                                            </span>
                                                            <ErrorMessage name="brand">
                                                                {(msg) => (
                                                                    <div className="invalid-feedback d-block">
                                                                        {msg}
                                                                    </div>
                                                                )}
                                                            </ErrorMessage>
                                                        </>
                                                    )}
                                                </Col>
                                            </Row>
                                            <Row className="pt-3">
                                                <Col
                                                    sm={3}
                                                    xs={existingProduct ? 4 : 12}
                                                    className={`pe-0 d-flex align-items-center ${existingProduct ? "" : "mb-2 mb-sm-0"}`}
                                                >
                                                    <label htmlFor="addProductListModal-category-field">
                                                        <strong
                                                            className={`text-dark fs-7 ${existingProduct ? "" : "required-label"}`}
                                                        >
                                                            {t("ProductsList.Category")}
                                                        </strong>
                                                    </label>
                                                </Col>
                                                <Col sm={9} xs={existingProduct ? 8 : 12} className="text-dark">
                                                    {existingProduct ? (
                                                        <span className="fs-7">
                                                            {existingProduct?.productCategoryNew?.name}
                                                        </span>
                                                    ) : (
                                                        <>
                                                            <div>
                                                                <span
                                                                    className={`${selectedCategory ? "me-4" : "d-none"}  mb-2 mb-lg-0 text-dark`}
                                                                >
                                                                    {selectedCategory}{" "}
                                                                    {selectedSubcategory ? (
                                                                        <>
                                                                            <i className="bi bi-arrow-right"></i>{" "}
                                                                            {selectedSubcategory}
                                                                        </>
                                                                    ) : null}
                                                                </span>
                                                                <Button
                                                                    ref={buttonCategoryRef}
                                                                    variant={`${touched.category && errors.category ? "outline-danger" : "outline-primary"}`}
                                                                    className="w-100 fw-normal py-1"
                                                                    id="addProductListModal-category-field"
                                                                    style={{ maxWidth: "149px" }}
                                                                    onClick={() => {
                                                                        setIsProductCategoryModalOpen(true);
                                                                    }}
                                                                >
                                                                    {categoryText[0]
                                                                        ? t("Change Category")
                                                                        : t("Select Category")}
                                                                </Button>
                                                                <ErrorMessage name="category">
                                                                    {(msg) => (
                                                                        <div className="text-danger fs-7 mt-1">
                                                                            {msg}
                                                                        </div>
                                                                    )}
                                                                </ErrorMessage>
                                                            </div>
                                                        </>
                                                    )}
                                                </Col>
                                            </Row>
                                            <Row className="pt-3">
                                                <Col
                                                    sm={3}
                                                    xs={existingProduct ? 4 : 12}
                                                    className={`pe-0 d-flex align-items-center ${existingProduct ? "" : "mb-2 mb-sm-0"}`}
                                                >
                                                    <label htmlFor="sku-addProduct">
                                                        <strong className="text-dark fs-7">
                                                            {t("ProductsList.SKU")}
                                                        </strong>
                                                    </label>
                                                </Col>
                                                <Col sm={9} xs={existingProduct ? 8 : 12} className="text-dark">
                                                    {existingProduct ? (
                                                        <span className="fs-7">{values?.sku ? values?.sku : "-"}</span>
                                                    ) : (
                                                        <>
                                                            <Field
                                                                name="sku"
                                                                id="sku-addProduct"
                                                                className={`form-control fs-7 ${touched.sku && errors.sku ? "is-invalid" : ""}`}
                                                                placeholder={t("ProductsList.SKU")}
                                                                maxLength={50}
                                                            />
                                                        </>
                                                    )}
                                                </Col>
                                            </Row>
                                            <Row className="p-0">
                                                <Col
                                                    sm={3}
                                                    xs={existingProduct ? 4 : 12}
                                                    className="pe-0 d-flex align-items-center"
                                                ></Col>
                                                <Col sm={9} xs={existingProduct ? 8 : 12} className="text-dark ">
                                                    {existingProduct ? (
                                                        ""
                                                    ) : (
                                                        <>
                                                            <span
                                                                className={`fs-7 ${values?.sku?.length > 50 ? "text-danger" : "text-grey"} `}
                                                            >
                                                                {values?.sku?.length}/50
                                                            </span>
                                                            <ErrorMessage name="sku">
                                                                {(msg) => (
                                                                    <div className="invalid-feedback d-block">
                                                                        {msg}
                                                                    </div>
                                                                )}
                                                            </ErrorMessage>
                                                        </>
                                                    )}
                                                </Col>
                                            </Row>
                                            <Row className="pt-3">
                                                <Col
                                                    sm={3}
                                                    xs={existingProduct ? 4 : 12}
                                                    className={`pe-0 d-flex align-items-center ${existingProduct ? "" : "mb-2 mb-sm-0"}`}
                                                >
                                                    <label htmlFor="barcode-addProduct">
                                                        <strong className="text-dark fs-7">
                                                            {t("ProductsList.Barcode")}
                                                        </strong>
                                                    </label>
                                                </Col>
                                                <Col sm={9} xs={existingProduct ? 8 : 12} className="text-dark">
                                                    {existingProduct ? (
                                                        <span className="fs-7">
                                                            {values?.barcode ? values?.barcode : "-"}
                                                        </span>
                                                    ) : (
                                                        <>
                                                            <Field
                                                                name="barcode"
                                                                id="barcode-addProduct"
                                                                className={`form-control fs-7 ${touched.barcode && errors.barcode ? "is-invalid" : ""}`}
                                                                placeholder={t("ProductsList.Barcode")}
                                                                maxLength={50}
                                                            />
                                                        </>
                                                    )}
                                                </Col>
                                            </Row>
                                            <Row className="p-0">
                                                <Col
                                                    sm={3}
                                                    xs={existingProduct ? 4 : 12}
                                                    className="pe-0 d-flex align-items-center"
                                                ></Col>
                                                <Col sm={9} xs={existingProduct ? 8 : 12} className="text-dark ">
                                                    {existingProduct ? (
                                                        ""
                                                    ) : (
                                                        <>
                                                            <span
                                                                className={`fs-7 ${values?.barcode?.length > 50 ? "text-danger" : "text-grey"} `}
                                                            >
                                                                {values?.barcode?.length}/50
                                                            </span>
                                                            <ErrorMessage name="barcode">
                                                                {(msg) => (
                                                                    <div className="invalid-feedback d-block">
                                                                        {msg}
                                                                    </div>
                                                                )}
                                                            </ErrorMessage>
                                                        </>
                                                    )}
                                                </Col>
                                            </Row>
                                            {!existingProduct ? (
                                                <>
                                                    <Row className="my-3">
                                                        <Col sm={3} className="mb-2 mb-sm-0 d-flex">
                                                            <label
                                                                htmlFor="AddProductListModal-unitForSale-field"
                                                                className="h-100 d-flex align-items-center text-dark fs-7"
                                                                style={{ maxHeight: "49px" }}
                                                            >
                                                                <strong className="required-label">
                                                                    {t("Unit of Measure for sale")}
                                                                </strong>
                                                            </label>
                                                        </Col>
                                                        <Col sm={9} md={3} className="mb-3 mb-md-0">
                                                            <ReactSelect
                                                                id="AddProductListModal-unitForSale-field"
                                                                name="unitForSale"
                                                                placeholder={t("Select an unit")}
                                                                options={unitOptions}
                                                                value={unitOptions?.find(
                                                                    (option: Option) =>
                                                                        option.value === values?.unitForSale
                                                                )}
                                                                onChange={(selectedOption: Option) =>
                                                                    handleMainUnit(
                                                                        selectedOption,
                                                                        setFieldValue,
                                                                        setFieldTouched,
                                                                        setFieldError
                                                                    )
                                                                }
                                                                isSearchable={false}
                                                            />
                                                            <ErrorMessage name="unitForSale">
                                                                {(msg) => (
                                                                    <div className="invalid-feedback d-block">
                                                                        {msg}
                                                                    </div>
                                                                )}
                                                            </ErrorMessage>
                                                        </Col>
                                                        <Col
                                                            sm={3}
                                                            md={2}
                                                            className="mb-2 d-flex px-md-0 mt-sm-2 my-md-0"
                                                        >
                                                            <label
                                                                htmlFor="AddProductListModal-alternativeUnitGroup-field"
                                                                className="h-100 d-flex align-items-center text-dark fs-7 text-break"
                                                                style={{ maxHeight: "49px" }}
                                                            >
                                                                <strong className="required-label">
                                                                    {t("Alternative measuring unit")}
                                                                </strong>
                                                            </label>
                                                        </Col>
                                                        <Col sm={9} md={4} className="mb-3 mb-md-0">
                                                            <ReactSelect
                                                                id="AddProductListModal-alternativeUnitGroup-field"
                                                                name="alternativeUnitGroupInput.alternativeUnitGroup"
                                                                placeholder={t("Select type of measure")}
                                                                options={alternativeUnitGroupOptions}
                                                                value={
                                                                    selectedGroupId == null
                                                                        ? null
                                                                        : (alternativeUnitGroupOptions?.find(
                                                                              (option) =>
                                                                                  option.value === selectedGroupId
                                                                          ) ?? null)
                                                                }
                                                                onChange={(selectedOption: Option) =>
                                                                    handleAlternativeUnitGroupChange(
                                                                        selectedOption,
                                                                        setFieldValue,
                                                                        setFieldTouched,
                                                                        setFieldError,
                                                                        values?.unitForSale
                                                                    )
                                                                }
                                                                isSearchable={false}
                                                            />
                                                            <span className="fs-7 mt-1 text-grey">
                                                                {t("for ex. weight")}
                                                            </span>
                                                            <ErrorMessage name="alternativeUnitGroupInput.alternativeUnitGroup">
                                                                {(msg) => (
                                                                    <div className="invalid-feedback d-block">
                                                                        {msg}
                                                                    </div>
                                                                )}
                                                            </ErrorMessage>
                                                        </Col>
                                                    </Row>

                                                    <Row className="mb-3">
                                                        <Col sm={3} className="mb-2 mb-sm-0">
                                                            <label
                                                                htmlFor="AddProductListModal-price-field"
                                                                className="text-dark fs-7 fw-bold me-3 h-100 d-flex-column align-items-center"
                                                                style={{ maxHeight: "49px" }}
                                                            >
                                                                <span className="required-label">
                                                                    {t("Selling price")}
                                                                </span>
                                                                <span className="d-block">
                                                                    {t(
                                                                        getUnitLabelByValue(
                                                                            unitOptions,
                                                                            values?.unitForSale,
                                                                            "sellingPrice"
                                                                        )
                                                                    )}
                                                                </span>
                                                            </label>
                                                        </Col>
                                                        <Col sm={9} md={3} className="mb-3 mb-md-0">
                                                            <PriceInput
                                                                name="price"
                                                                placeholder="0.00"
                                                                id="addProduct-price-field"
                                                            />
                                                        </Col>
                                                        <Col sm={3} md={2} className="mb-2 my-md-0 mt-sm-2">
                                                            <label
                                                                htmlFor="addProductListModal-cost-field"
                                                                className="text-dark fs-7 fw-bold h-100 d-flex align-items-center"
                                                                style={{ maxHeight: "49px" }}
                                                            >
                                                                <span>
                                                                    {t("Cost price 1 {{unit}}", {
                                                                        unit: t(
                                                                            getUnitLabelByValue(
                                                                                unitOptions,
                                                                                values?.unitForSale,
                                                                                "costPrice"
                                                                            )
                                                                        ),
                                                                    })}
                                                                </span>
                                                            </label>
                                                        </Col>
                                                        <Col sm={9} md={4}>
                                                            <PriceInput
                                                                name="cost"
                                                                placeholder="0.00"
                                                                id="addProductListModal-cost-field"
                                                            />
                                                        </Col>
                                                    </Row>

                                                    <Row className="mb-3">
                                                        <Col sm={3} className="mb-2 mb-sm-0 d-flex">
                                                            <label
                                                                htmlFor="addProductListModal-alternativeUnitGroupInput.alternativeValue-field"
                                                                className="text-dark fs-7 fw-bold me-3 h-100 d-flex align-items-center"
                                                                style={{ whiteSpace: "nowrap", maxHeight: "49px" }}
                                                            >
                                                                <span className="required-label">
                                                                    {t(
                                                                        getUnitLabelByValue(
                                                                            unitOptions,
                                                                            values?.unitForSale,
                                                                            "quantity"
                                                                        )
                                                                    )}
                                                                </span>
                                                            </label>
                                                        </Col>
                                                        <Col sm={9} md={5} className="mb-3 mb-md-0 pe-md-0">
                                                            <NumberInput
                                                                id="addProductListModal-alternativeUnitGroupInput.alternativeValue-field"
                                                                name="alternativeUnitGroupInput.alternativeValue"
                                                                placeholder={t("Add quantity")}
                                                                disabled={disableAltFields}
                                                                maxLength={9}
                                                            />
                                                        </Col>
                                                        <Col sm={9} md={4} className="mb-2 mb-sm-0 ms-auto">
                                                            <ReactSelect
                                                                id="addProductListModal-alternativeUnit-field"
                                                                name="alternativeUnitGroupInput.alternativeUnit"
                                                                placeholder={t("Select an unit")}
                                                                options={alternativeUnitOptions}
                                                                value={alternativeUnitOptions?.find(
                                                                    (option: Option) =>
                                                                        option.value ===
                                                                        values?.alternativeUnitGroupInput
                                                                            ?.alternativeUnit
                                                                )}
                                                                onChange={(selectedOption: Option) =>
                                                                    handleAlternativeUnitChange(
                                                                        selectedOption,
                                                                        setFieldValue
                                                                    )
                                                                }
                                                                isSearchable={false}
                                                                disabled={disableAltFields}
                                                            />
                                                            <ErrorMessage name="alternativeUnitGroupInput.alternativeUnit">
                                                                {(msg) => (
                                                                    <div className="invalid-feedback d-block">
                                                                        {msg}
                                                                    </div>
                                                                )}
                                                            </ErrorMessage>
                                                        </Col>
                                                    </Row>

                                                    <Row className="mb-2">
                                                        <Col sm={3} className="mb-2 mb-sm-0">
                                                            <label
                                                                htmlFor="addProductListModal-quantity-field"
                                                                className="text-dark fs-7 fw-bold me-3 h-100 d-flex align-items-center"
                                                                style={{ maxHeight: "49px" }}
                                                            >
                                                                <span className="required-label text-break">
                                                                    {t("Add to stock")}
                                                                </span>
                                                            </label>
                                                        </Col>
                                                        <Col sm={9} md={5} className="mb-3 mb-md-0 pe-md-0">
                                                            <NumberInput
                                                                id="addProductListModal-quantity-field"
                                                                name="quantity"
                                                                placeholder={t("Add quantity")}
                                                                maxLength={9}
                                                                disabled={isGroupNull}
                                                            />
                                                        </Col>
                                                        <Col sm={9} md={4} className="ms-auto">
                                                            <ReactSelect
                                                                id="addProductListModal-unit-field"
                                                                name="unit"
                                                                placeholder={t("Select an unit")}
                                                                options={combinedUnitOptions}
                                                                value={alternativeUnitOptions?.find(
                                                                    (option: Option) => option.value === values?.unit
                                                                )}
                                                                onChange={(selectedOption: Option) =>
                                                                    setFieldValue("unit", selectedOption.value)
                                                                }
                                                                isSearchable={false}
                                                                disabled={isGroupNull}
                                                            />
                                                            <ErrorMessage name="unit">
                                                                {(msg) => (
                                                                    <div className="invalid-feedback d-block">
                                                                        {msg}
                                                                    </div>
                                                                )}
                                                            </ErrorMessage>
                                                        </Col>
                                                    </Row>
                                                </>
                                            ) : null}
                                            {existingProduct ? (
                                                <>
                                                    <Row className="pt-3">
                                                        <Col sm={3} className="pe-0 d-flex align-items-center">
                                                            <label htmlFor="sku-addProduct">
                                                                <strong className="text-dark fs-7">
                                                                    {t("Unit group")}
                                                                </strong>
                                                            </label>
                                                        </Col>
                                                        <Col sm={9} className="text-dark">
                                                            <div className="pt-2">
                                                                <span className="fs-7">{values?.unitGroup}</span>
                                                            </div>
                                                        </Col>
                                                    </Row>
                                                    <Row className="py-2 d-flex align-items-center">
                                                        <Col sm={3} className="pe-0 pt-2">
                                                            <label htmlFor="quantity-addProduct">
                                                                <strong
                                                                    className={`text-dark fs-7 ${existingProduct ? "" : "required-label"}`}
                                                                >
                                                                    {t("Remnants")}
                                                                </strong>
                                                            </label>
                                                        </Col>
                                                        <Col sm={9} className="text-dark">
                                                            <div className="pt-2">
                                                                <span className="fs-7">
                                                                    {existingProduct?.remnantsPerBranch}
                                                                </span>
                                                            </div>
                                                        </Col>
                                                    </Row>
                                                    <Row className="py-2">
                                                        <Col sm={3} className="pe-3 mb-2 mb-sm-0">
                                                            <label
                                                                htmlFor="addProductListModal-quantity-addProduct"
                                                                className="h-100 d-flex align-items-center"
                                                                style={{ maxHeight: "49px" }}
                                                            >
                                                                <strong className="text-dark fs-7 required-label">
                                                                    {t("Amount to be added")}
                                                                </strong>
                                                            </label>
                                                        </Col>
                                                        <Col sm={9} className="text-dark">
                                                            <QuantityInput
                                                                name="quantity"
                                                                id="addProductListModal-quantity-field"
                                                                placeholder={t("Amount to be added")}
                                                                maxLength={9}
                                                            />
                                                            <ErrorMessage name="quantity">
                                                                {(msg) => (
                                                                    <div className="invalid-feedback d-block">
                                                                        {msg}
                                                                    </div>
                                                                )}
                                                            </ErrorMessage>
                                                        </Col>
                                                    </Row>
                                                    <Row className="py-2">
                                                        <Col sm={3} className="pe-0 mb-2 mb-sm-0">
                                                            <label
                                                                htmlFor="addProductListModal-quantity-addProduct"
                                                                className="h-100 d-flex align-items-center fw-bold text-dark fs-7"
                                                                style={{ maxHeight: "49px" }}
                                                            >
                                                                <span className="required-label text-break">
                                                                    {t("Unit of measurements")}
                                                                </span>
                                                            </label>
                                                        </Col>
                                                        <Col sm={9} className="text-dark">
                                                            <ReactSelect
                                                                id="addProductListModal-unit-field"
                                                                name="unit"
                                                                placeholder={t("Select an unit")}
                                                                options={unitOfMeasurements}
                                                                value={unitOfMeasurements?.find(
                                                                    (option: Option) => option.value === values.unit
                                                                )}
                                                                onChange={(selectedOption: Option) =>
                                                                    setFieldValue("unit", selectedOption.value)
                                                                }
                                                                isSearchable={false}
                                                            />
                                                            <ErrorMessage name="unit">
                                                                {(msg) => (
                                                                    <div className="invalid-feedback d-block">
                                                                        {msg}
                                                                    </div>
                                                                )}
                                                            </ErrorMessage>
                                                        </Col>
                                                    </Row>
                                                </>
                                            ) : null}
                                            <Row className="py-2 d-flex align-items-center">
                                                <Col sm={3} className="d-flex pr-0 mb-2 mb-sm-0">
                                                    <label
                                                        htmlFor="supplier"
                                                        className="mb-0 text-dark form-label text-break"
                                                    >
                                                        <span className="text-dark fw-bold mt-1 fs-7">
                                                            {t("Supplier")}
                                                        </span>
                                                    </label>
                                                </Col>
                                                <Col sm={9}>
                                                    <CustomCreatableReactSelect
                                                        optionsArray={productSupplierOptions}
                                                        placeholder={t("Select supplier")}
                                                        setField={setFieldValue}
                                                        fieldName="supplier"
                                                        value="name"
                                                        isTooltip={false}
                                                        name={"supplier"}
                                                        id={"addProduct-supplier-field"}
                                                        maxLength={100}
                                                        createOptionLabel={t("Create a supplier")}
                                                        noOptionsText={t("Start typing the supplier name")}
                                                        createOnBlur={true}
                                                    />
                                                    <ErrorMessage
                                                        name="supplier"
                                                        component="div"
                                                        className="fs-7 m-0 p-1 text-danger"
                                                    />
                                                </Col>
                                            </Row>
                                            <Row className="pt-2">
                                                <Col sm={3} className="pr-0 mb-2 mb-sm-0">
                                                    <label
                                                        htmlFor="comment"
                                                        className="my-0 pt-sm-3 text-dark form-label text-break text-dark fw-bold fs-7 line-height-140"
                                                    >
                                                        {t("Comment")}
                                                    </label>
                                                </Col>
                                                <Col sm={9}>
                                                    <Field
                                                        as="textarea"
                                                        name="comment"
                                                        placeholder={t("For example, enter the invoice number")}
                                                        className={`fs-7 form-control ${errors?.comment ? "is-invalid" : ""}`}
                                                        maxLength={400}
                                                    />
                                                    <span
                                                        className={`fs-7 ${values.comment?.length > 400 ? "text-danger" : "text-grey"}`}
                                                    >
                                                        {values.comment?.length}/400
                                                    </span>
                                                    <ErrorMessage
                                                        name="comment"
                                                        component="div"
                                                        className="text-danger"
                                                    />
                                                </Col>
                                            </Row>
                                        </Modal.Body>
                                        <div className="p-6">
                                            <Row className="d-flex justify-content-end pt-6">
                                                <Col xs={6} sm="auto" className="mb-sm-0 pe-1">
                                                    <Button
                                                        variant="outline-primary"
                                                        className="custom-btn fw-normal"
                                                        onClick={() => handleResetForm(resetForm)}
                                                    >
                                                        {t("Cancel")}
                                                    </Button>
                                                </Col>
                                                <Col xs={6} sm="auto" className="mb-sm-0 ps-1">
                                                    <Button
                                                        type="submit"
                                                        variant="primary"
                                                        className="custom-btn text-truncate"
                                                        disabled={isSubmitting || !canSubmit}
                                                    >
                                                        {isSubmitting ? (
                                                            <SpinnerLoader
                                                                variant="light"
                                                                loadingText={t("Adding...")}
                                                            />
                                                        ) : (
                                                            t("Add")
                                                        )}
                                                    </Button>
                                                </Col>
                                            </Row>
                                        </div>
                                    </Form>
                                </div>
                            </Modal>
                        </>
                    );
                }}
            </Formik>
            {isProductCategoryModalOpen && productCategoriesData && !productCategoryLoading ? (
                <ServiceModalWrapper
                    companyId={Number(currentCompanyId)}
                    branchId={Number(branchId)}
                    i18n={i18next}
                    selectedCategoryId={null}
                    selectedSubcategoryId={null}
                    apolloUrl={process.env.REACT_APP_API_URL}
                    isOpen={isProductCategoryModalOpen}
                    onClose={() => {
                        setIsProductCategoryModalOpen(!isProductCategoryModalOpen);
                    }}
                    onCategorySelect={(selectedCategory) => {
                        handleCategoryNameChange(selectedCategory?.name);
                        setPickedCategoryId(selectedCategory?.id);
                        setIsProductCategoryModalOpen(!isProductCategoryModalOpen);
                    }}
                    onSubcategorySelect={(selectedSubcategory) => {
                        handleSubCategoryNameChange(
                            selectedSubcategory?.name,
                            selectedSubcategory?.parentCategory?.name
                        );
                        setPickedCategoryId(selectedSubcategory?.id);
                        setIsProductCategoryModalOpen(!isProductCategoryModalOpen);
                    }}
                    categoryList={categoryList}
                    categoryInput={categoryInput}
                    categoryListData={categoryListData}
                    refetchCategories={refetchCategories}
                    onSubmitCreate={handleSubmitCreateCategory}
                    canCreateCategory={canCreateProductCategory}
                    currentCategory={currentCategory}
                    categoryService={CategoryProduct}
                    requestInputService={RequestInputService}
                    normalizeCategoryData={normalizeCategoryData}
                />
            ) : null}
        </>
    );
};

export default AddProductListModal;
